// Panel scenarios — guided exercises that drive the FACU simulator.
// Each scenario has steps; player must perform the expected action to advance.
//
// Action verbs: ack, silence, reset, drill, test, trigger:<eventId>, observe
// Events: pull-101, smoke-201, ground-fault, ac-loss, low-battery,
//         supervisory-tamper, waterflow, drill-test, sprinkler-supv

export const PANEL_EVENTS = {
  "pull-101": {
    label: "Pull Station P-101 activated",
    state: "alarm",
    addr: "L1-D012",
    desc: "MANUAL PULL — Lobby east exit",
    nfpa: "§17.14.8 — pulls within 5 ft of every exit door.",
  },
  "smoke-201": {
    label: "Smoke detector S-201 alarm",
    state: "alarm",
    addr: "L1-D045",
    desc: "PHOTO SMOKE — Floor 2 corridor",
    nfpa: "§17.7 — smoke detector spacing 30 ft on-center default.",
  },
  "ground-fault": {
    label: "Ground fault on SLC loop 1",
    state: "trouble",
    addr: "SYS",
    desc: "GROUND FAULT — Loop 1",
    nfpa: "§10.17 — system shall annunciate ground fault as trouble.",
  },
  "ac-loss": {
    label: "AC primary power lost",
    state: "trouble",
    addr: "SYS",
    desc: "AC POWER LOSS — Running on batteries",
    nfpa: "§10.6.7.2 — secondary supply ≥ 24 hr standby + 5 min alarm.",
  },
  "low-battery": {
    label: "Standby battery low voltage",
    state: "trouble",
    addr: "SYS",
    desc: "LOW BATTERY — < 22.0 VDC",
    nfpa: "§10.6.10 — battery shall be supervised; depleted state annunciated.",
  },
  "supervisory-tamper": {
    label: "Sprinkler valve tamper TS-1",
    state: "supervisory",
    addr: "L2-D003",
    desc: "VALVE CLOSED — Main control valve",
    nfpa: "§17.16 — supervisory signals are distinct from alarm.",
  },
  waterflow: {
    label: "Waterflow switch FS-1 activated",
    state: "alarm",
    addr: "L2-D001",
    desc: "WATERFLOW — Sprinkler riser",
    nfpa: "§17.12 — waterflow alarm w/ retard 0-90 sec.",
  },
  "drill-test": {
    label: "Drill activated by command",
    state: "alarm",
    addr: "SYS",
    desc: "FIRE DRILL — Manual",
    nfpa: "§14.4 — drills are documented, not subject to ITM acceptance.",
  },
};

export const PANEL_SCENARIOS = [
  // ─── LEVEL 1 (5 scenarios) ──────────────────────────────────────────────
  {
    id: "s1-basic-alarm",
    level: 1,
    title: "Acknowledge a pull-station alarm",
    blurb: "A pull station has been activated in the lobby. Acknowledge, silence, then reset the system.",
    steps: [
      { kind: "trigger", event: "pull-101", prompt: "Event injected: pull station activated. Watch the panel transition." },
      { kind: "action", verb: "ack", prompt: "Press ACK to acknowledge the active alarm.", hint: "Acknowledging silences the panel piezo and accepts the active condition. Reference: §10.17.1." },
      { kind: "action", verb: "silence", prompt: "Press SILENCE to silence notification appliances after first responders arrive.", hint: "Silence stops horns/strobes but keeps the alarm condition latched." },
      { kind: "action", verb: "reset", prompt: "Press RESET to clear the alarm after the pull station is restored.", hint: "Reset cycles the panel; if the device is still active, alarm returns." },
    ],
  },
  {
    id: "s2-trouble-first",
    level: 1,
    title: "Diagnose a ground fault before it becomes an alarm",
    blurb: "A ground fault appears on Loop 1. Acknowledge the trouble; verify it does NOT cause notification appliance activation.",
    steps: [
      { kind: "trigger", event: "ground-fault", prompt: "Ground fault injected on SLC Loop 1." },
      { kind: "observe", prompt: "Note: TROUBLE LED is amber, not the red ALARM LED. Notification appliances are silent." },
      { kind: "action", verb: "ack", prompt: "Press ACK to acknowledge the trouble (silences only the panel piezo)." },
      { kind: "action", verb: "reset", prompt: "Press RESET. Trouble persists because the underlying ground fault hasn't been cleared in this simulation — observe behavior." },
    ],
  },
  {
    id: "s3-ac-loss",
    level: 1,
    title: "AC power loss — battery operation",
    blurb: "Building loses utility power. The panel transitions to standby battery. Verify trouble + recover.",
    steps: [
      { kind: "trigger", event: "ac-loss", prompt: "AC primary power lost." },
      { kind: "observe", prompt: "AC POWER LED extinguishes, TROUBLE LED illuminates. System runs from sealed lead-acid batteries." },
      { kind: "action", verb: "ack", prompt: "Acknowledge the trouble." },
      { kind: "observe", prompt: "Standby battery must support 24 hr supervision + 5 min alarm per NFPA 72 §10.6.7.2." },
    ],
  },
  {
    id: "s4-supervisory",
    level: 1,
    title: "Supervisory signal — tamper switch",
    blurb: "A sprinkler control valve was closed for maintenance. Recognize SUPERVISORY (yellow) vs ALARM (red).",
    steps: [
      { kind: "trigger", event: "supervisory-tamper", prompt: "Tamper switch TS-1 activated — main valve closed." },
      { kind: "observe", prompt: "SUPERVISORY LED — distinct from alarm. No NAC activation. §17.16." },
      { kind: "action", verb: "ack", prompt: "Acknowledge the supervisory signal." },
      { kind: "action", verb: "reset", prompt: "After valve is restored, reset clears the supervisory state." },
    ],
  },
  {
    id: "s5-drill",
    level: 1,
    title: "Conduct a fire drill",
    blurb: "Building owner is running a quarterly drill. Activate DRILL mode, observe NAC, and reset.",
    steps: [
      { kind: "action", verb: "drill", prompt: "Press DRILL to start a manual drill — NACs activate without an actual alarm initiation." },
      { kind: "observe", prompt: "Notification appliances activate; system marks the event as DRILL not ALARM in history." },
      { kind: "action", verb: "silence", prompt: "After drill duration, SILENCE to stop NACs." },
      { kind: "action", verb: "reset", prompt: "RESET to return to normal." },
    ],
  },

  // ─── LEVEL 2 (5 scenarios) ──────────────────────────────────────────────
  {
    id: "s6-multi-alarm",
    level: 2,
    title: "Coincidence: two alarms + AC loss simultaneously",
    blurb: "Real incident: smoke alarm activates, waterflow follows, then AC drops. Triage in priority order.",
    steps: [
      { kind: "trigger", event: "smoke-201", prompt: "Smoke detector S-201 alarm." },
      { kind: "trigger", event: "waterflow", prompt: "Waterflow switch FS-1 activates 12 seconds later." },
      { kind: "trigger", event: "ac-loss", prompt: "AC power lost during the incident." },
      { kind: "action", verb: "ack", prompt: "Acknowledge — multiple events queued. Highest-priority (alarm) shown first." },
      { kind: "observe", prompt: "Per §10.16, alarm > supervisory > trouble. Display cycles all active events." },
      { kind: "action", verb: "silence", prompt: "Once responders confirm scene, silence NACs." },
      { kind: "action", verb: "reset", prompt: "Reset only after devices restored AND AC restored." },
    ],
  },
  {
    id: "s7-low-battery-ack",
    level: 2,
    title: "Low-battery during AC loss",
    blurb: "After 22+ hours on batteries, voltage drops below threshold. System shall annunciate.",
    steps: [
      { kind: "trigger", event: "ac-loss", prompt: "AC lost — running on batteries." },
      { kind: "trigger", event: "low-battery", prompt: "Battery voltage drops below 22.0 VDC after extended run." },
      { kind: "action", verb: "ack", prompt: "Acknowledge low-battery trouble — separate event from AC loss." },
      { kind: "observe", prompt: "Per §10.6.10 + §10.17, both states must be discretely annunciated. Operator must know which to address first (battery → AC → restore)." },
    ],
  },
  {
    id: "s8-test-mode",
    level: 2,
    title: "Place device in test mode",
    blurb: "ITM week: technician needs to put L1-D045 in test before triggering it for sensitivity check.",
    steps: [
      { kind: "action", verb: "test", prompt: "Press TEST to put the panel in test mode for the selected zone/device." },
      { kind: "observe", prompt: "TEST mode bypasses NAC + central-station transmission for that zone — but other zones remain active." },
      { kind: "trigger", event: "smoke-201", prompt: "Test the smoke detector — it activates locally but central station receives 'Test' not 'Alarm'." },
      { kind: "action", verb: "reset", prompt: "Reset returns the test device to normal supervision." },
      { kind: "observe", prompt: "Per §14.4 Table 14.4.3.2, smoke sensitivity is verified every 2 yrs and replaced if out of listed range." },
    ],
  },
  {
    id: "s9-silence-discipline",
    level: 2,
    title: "When NOT to silence",
    blurb: "First-due engine arrives 90 sec after activation; building manager wants 'the noise off.' Discipline check.",
    steps: [
      { kind: "trigger", event: "smoke-201", prompt: "Smoke alarm — confirmed real, not nuisance." },
      { kind: "observe", prompt: "DO NOT SILENCE before fire department command. Silence too early signals 'all clear' to occupants and slows evacuation. §10.18 + AHJ policy." },
      { kind: "action", verb: "ack", prompt: "Ack the alarm to suppress only the panel piezo. NACs continue." },
      { kind: "observe", prompt: "Wait for FD command before silencing. Document time-stamps in the event log." },
      { kind: "action", verb: "silence", prompt: "FD on scene — now silence is appropriate." },
      { kind: "action", verb: "reset", prompt: "After overhaul, reset to clear alarm." },
    ],
  },
  {
    id: "s10-cascade",
    level: 2,
    title: "Networked cascade — smoke + tamper + AC drop",
    blurb: "Stress test: alarm + supervisory + trouble all queued, with priorities preserved on the display.",
    steps: [
      { kind: "trigger", event: "supervisory-tamper", prompt: "Tamper detected — supervisory." },
      { kind: "trigger", event: "smoke-201", prompt: "Smoke alarm activates 30 sec later." },
      { kind: "trigger", event: "ac-loss", prompt: "AC drops at the same time." },
      { kind: "observe", prompt: "Display priority: ALARM (red) shown first. SUPERVISORY (yellow) and TROUBLE (amber) queued." },
      { kind: "action", verb: "ack", prompt: "Ack walks through events one at a time per §10.17.2." },
      { kind: "action", verb: "silence", prompt: "Silence after FD on scene." },
      { kind: "action", verb: "reset", prompt: "Reset after all initiating devices restored." },
    ],
  },
];
